// Design tokens — Cabinet Base
// ----------------------------------------------------------------------
// The one source for colours, shadows, radii and sizes, mirrored from the
// Cabinet Base design system (https://claude.ai/artifact/69dKs1nZE6dYyJCummpc6A).
// Names are the design system's own, so a value here can be found there by
// name and the two kept in step. palette.js and customShadows.js translate
// these into MUI's shape; nothing else should hold a literal colour.
//
// Light is the theme every brand ships. Dark is optional in the design
// system; this app offers it through the header toggle (ColorModeProvider).
//
// Every text pair clears WCAG AA (4.5:1) in both themes, and focus rings and
// meaningful marks clear 3:1. The ratios live in the design system's usage
// notes; check a new pair in both themes before adding it.
// ----------------------------------------------------------------------

export const GREY = {
    0: '#FFFFFF',
    100: '#F9FAFB',
    200: '#F4F6F8',
    300: '#DFE3E8',
    400: '#C4CDD5',
    500: '#919EAB',
    600: '#637381',
    700: '#454F5B',
    800: '#212B36',
    900: '#161C24'
};

// Ramps run lighter → darker. In dark each one steps two rungs lighter:
// `main` becomes the tint that reads on a dark surface, and the deep values
// move down to `dark`/`darker`, where they still work as fills.
const RAMPS = {
    light: {
        primary: ['#D1E9FC', '#76B0F1', '#2065D1', '#103996', '#061B64'],
        secondary: ['#D6E4FF', '#84A9FF', '#3366FF', '#1939B7', '#091A7A'],
        info: ['#E0F2FE', '#38BDF8', '#0369A1', '#075985', '#0C4A6E'],
        success: ['#DCFCE7', '#4ADE80', '#15803D', '#14532D', '#052E16'],
        warning: ['#FEF3C7', '#FBBF24', '#B45309', '#78350F', '#451A03'],
        error: ['#FEE2E2', '#F87171', '#B91C1C', '#7F1D1D', '#450A0A']
    },
    dark: {
        primary: ['#D1E9FC', '#A4CCF6', '#76B0F1', '#2065D1', '#103996'],
        secondary: ['#D6E4FF', '#ADC6FF', '#84A9FF', '#3366FF', '#1939B7'],
        info: ['#BAE6FD', '#7DD3FC', '#38BDF8', '#0369A1', '#075985'],
        success: ['#BBF7D0', '#86EFAC', '#4ADE80', '#15803D', '#14532D'],
        warning: ['#FDE68A', '#FCD34D', '#FBBF24', '#B45309', '#78350F'],
        error: ['#FECACA', '#FCA5A5', '#F87171', '#B91C1C', '#7F1D1D']
    }
};

export const CHROMATIC = Object.keys(RAMPS.light);

const STEPS = ['lighter', 'light', 'main', 'dark', 'darker'];

/** `#RRGGBB` at `opacity`, as the rgba() string the design system stores. */
const withAlpha = (hex, opacity) => {
    const value = parseInt(hex.slice(1), 16);
    // eslint-disable-next-line no-bitwise
    return `rgba(${value >> 16},${(value >> 8) & 255},${value & 255},${opacity})`;
};

/**
 * Every colour token for one theme, keyed by its design-system name
 * (`primary-main`, `text-secondary`, `count-zero-text` …).
 */
export function colorTokens(mode = 'light') {
    const isLight = mode !== 'dark';
    const ramps = isLight ? RAMPS.light : RAMPS.dark;
    const tokens = {};

    CHROMATIC.forEach((color) => {
        STEPS.forEach((step, i) => {
            tokens[`${color}-${step}`] = ramps[color][i];
        });
        // Light mains are dark enough for white; dark mains are light tints.
        tokens[`on-${color}`] = isLight ? '#FFFFFF' : GREY[900];
        tokens[`${color}-soft`] = withAlpha(ramps[color][2], 0.16);
        tokens[`${color}-soft-text`] = isLight
            ? ramps[color][3]
            : ramps[color][1];
    });

    const primaryMain = tokens['primary-main'];
    const neutral = (opacity) => withAlpha(GREY[500], opacity);

    return {
        ...tokens,
        // `primary-dark` is too deep under the dark theme's `on-primary`.
        'primary-hover': isLight ? tokens['primary-dark'] : tokens['primary-light'],
        'common-black': '#000000',
        'common-white': '#FFFFFF',

        'text-primary': isLight ? GREY[800] : GREY[100],
        'text-secondary': isLight ? GREY[600] : GREY[400],
        'text-placeholder': isLight ? GREY[600] : GREY[400],
        'text-disabled': GREY[500],
        'text-success': isLight ? tokens['success-darker'] : tokens['success-light'],
        'text-error': isLight ? tokens['error-darker'] : tokens['error-light'],

        // Surfaces stack upwards in both themes: default → paper → neutral.
        'background-paper': isLight ? '#FFFFFF' : GREY[800],
        'background-default': isLight ? GREY[100] : GREY[900],
        // Dark: grey-500 at 16% flattened over dark paper.
        'background-neutral': isLight ? GREY[200] : '#333D49',
        'background-tooltip': isLight ? GREY[800] : GREY[700],

        // Grey 500 sits mid-ramp, so a translucent wash of it reads over
        // either a light or a dark surface.
        'divider': neutral(0.24),
        'border-input': neutral(0.32),
        'border-underline': neutral(0.56),
        'fill-input': neutral(0.12),
        'action-active': isLight ? GREY[600] : GREY[400],
        'action-hover': neutral(0.08),
        'action-selected': neutral(0.16),
        'action-focus': neutral(0.24),
        'action-disabled': neutral(0.8),
        'action-disabled-background': neutral(0.24),

        'primary-tint-6': withAlpha(primaryMain, 0.06),
        'primary-tint-12': withAlpha(primaryMain, 0.12),
        'primary-tint-18': withAlpha(primaryMain, 0.18),
        'primary-border': withAlpha(primaryMain, 0.45),
        'focus-halo': withAlpha(primaryMain, isLight ? 0.16 : 0.24),
        // Black in dark: grey 800 is the dark paper, and a scrim of it barely
        // darkens the page behind a dialog.
        'backdrop': withAlpha(isLight ? GREY[800] : '#000000', 0.8),

        'count-background': isLight ? GREY[200] : GREY[700],
        'count-text': isLight ? GREY[700] : GREY[100],
        'count-zero-border': isLight ? GREY[300] : GREY[600],
        'count-zero-text': isLight ? GREY[600] : GREY[400]
    };
}

/**
 * Named shadows for one theme. Light casts grey; dark casts black, since a
 * grey shadow disappears against a dark surface.
 */
export function shadowTokens(mode = 'light') {
    const colors = colorTokens(mode);
    const base = mode === 'dark' ? '#000000' : GREY[500];
    const shade = (opacity) => withAlpha(base, opacity);
    const tokens = {
        'shadow-z1': `0 1px 2px 0 ${shade(0.16)}`,
        'shadow-z4': `0 4px 8px 0 ${shade(0.16)}`,
        'shadow-z8': `0 8px 16px 0 ${shade(0.16)}`,
        'shadow-z12': `0 12px 24px -4px ${shade(0.16)}`,
        'shadow-z16': `0 16px 32px -4px ${shade(0.16)}`,
        'shadow-z20': `0 20px 40px -4px ${shade(0.16)}`,
        'shadow-z24': `0 24px 48px 0 ${shade(0.16)}`,
        'shadow-card': `0 0 2px 0 ${shade(0.2)}, 0 12px 24px -4px ${shade(0.12)}`,
        'shadow-dialog': `-40px 40px 80px -8px ${shade(0.24)}`,
        'shadow-dropdown': `0 0 2px 0 ${shade(0.24)}, -20px 20px 40px -4px ${shade(0.24)}`
    };
    CHROMATIC.forEach((color) => {
        tokens[`shadow-${color}`] = `0 8px 16px 0 ${withAlpha(colors[`${color}-main`], 0.24)}`;
    });
    return tokens;
}

// Px, as MUI's `shape` and `sx` take them.
export const RADIUS = {
    'radius-base': 6,
    'radius-option': 9,
    'radius-popover': 9,
    'radius-menu-item': 10,
    'radius-card': 12,
    'radius-toolbar-control': 12,
    'radius-toolbar-card': 16,
    'radius-pill': 999
};

export const SIZE = {
    'toolbar-control-height': 44,
    'toolbar-card-min-height': 70,
    'toolbar-badge-height': 20,
    'toolbar-reset-width': 40,
    'toolbar-reset-width-compact': 34,
    'button-height-large': 48,
    'label-height': 24,
    // Phones only: read by the mobile grid cards and sheets, which render
    // below the `sm` breakpoint, so they never reach the desktop layout.
    'touch-target': 44,
    'mobile-card-padding': 16,
    'mobile-card-gap': 12,
    'mobile-sheet-radius': 22
};
